import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import SeoHead from "../components/SeoHead";

const faqs = [
  {
    q: "What is a music bracket?",
    a: "A music bracket is a tournament-style competition where songs are paired against each other in head-to-head matchups. You listen to each pair and pick your favorite. Winners advance to the next round until one song is crowned the champion. It's inspired by sports tournament brackets like March Madness, but for music.",
  },
  {
    q: "How do I start a bracket?",
    a: "From the home page, choose a game mode — Artist Brackets, Custom Games, or Artist vs Artist. Then select your artist(s), pick a bracket size (8, 16, 32, or 64 songs), choose single or double elimination, and hit Start. Your bracket will be generated automatically.",
  },
  {
    q: "What is the difference between single and double elimination?",
    a: "In single elimination, a song is out after one loss. In double elimination, every song gets a second chance — it must lose twice before being eliminated. Double elimination produces more accurate rankings because a single upset doesn't knock out a strong song permanently.",
  },
  {
    q: "Can I listen to songs during the bracket?",
    a: "Yes. Each matchup includes 30-second song previews so you can listen before making your pick. This is especially helpful when comparing songs you haven't heard in a while or discovering deep cuts from an artist's catalog.",
  },
  {
    q: "How are songs selected for artist brackets?",
    a: "When you pick an artist, songs are pulled from our database of popular and catalog tracks. The selection is randomized within the bracket size you choose, so you'll get a different mix each time you play. This keeps the game fresh even for the same artist.",
  },
  {
    q: "What are custom games?",
    a: "Custom games build brackets from a random mix of songs across all artists and genres in our database. It's a great way to discover new music and compare songs you might never have put side by side.",
  },
  {
    q: "How does Artist vs Artist work?",
    a: "Artist vs Artist takes songs from two different artists and shuffles them into one bracket. Half the bracket comes from each artist, so matchups can be within the same artist or across both. It's the ultimate head-to-head showdown.",
  },
  {
    q: "Can I save and share my brackets?",
    a: "Yes. Sign in with your Google account to save completed brackets. You can also toggle them as public, which makes them visible on the Explore page where other players can discover and like them.",
  },
  {
    q: "What is the Explore page?",
    a: "The Explore page is a community feed of public brackets. You can browse recent and popular results, see which songs other players crowned as winners, and like brackets you find interesting. It's a way to discover new music through other people's tastes.",
  },
  {
    q: "Is Music Brackets free to use?",
    a: "Yes, Music Brackets is completely free. You can play unlimited brackets, explore community results, and save your own history without paying anything. We're supported by ads to keep the site running.",
  },
  {
    q: "Do I need an account to play?",
    a: "No. You can play brackets without signing in. An account is only needed if you want to save your results to the cloud and share them on the Explore page. Your game history is also saved locally in your browser.",
  },
  {
    q: "What genres and artists are available?",
    a: "Our database spans a wide range of genres including pop, rock, hip-hop, R&B, country, electronic, indie, classical, jazz, Latin, K-pop, and more. We're constantly adding new artists and songs. If your favorite artist isn't available yet, check back soon.",
  },
];

export default function AboutPage() {
  return (
    <Box id="about-page" sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      <SeoHead
        title="About"
        description="Learn about Music Brackets — the free tool for ranking songs through bracket-style tournaments. Discover how it works, game modes, and frequently asked questions."
        canonical="/about"
      />

      {/* Hero */}
      <Typography id="about-title" variant="h4" textAlign="center" gutterBottom>
        <InfoIcon sx={{ fontSize: 36, mr: 1, verticalAlign: "middle", color: "#3498db" }} />
        About Music Brackets
      </Typography>
      <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4, maxWidth: 650, mx: "auto" }}>
        Music Brackets is a free, interactive tool that lets you rank your favorite songs through
        bracket-style tournaments. Whether you want to find your number-one track from a single
        artist or pit two artists against each other, Music Brackets turns the debate into a game.
      </Typography>

      {/* What Is Music Brackets */}
      <Card sx={{ mb: 4, backgroundColor: "rgba(52,152,219,0.04)", border: "1px solid rgba(52,152,219,0.12)" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            What Is Music Brackets?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Music Brackets takes the excitement of tournament-style competition and applies it to
            music. Instead of teams playing games, songs go head-to-head in matchups decided by you.
            The concept is simple: listen, compare, and pick a winner. Repeat until one song stands
            above the rest.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The idea grew out of a common debate among music fans — "What's your favorite song by
            this artist?" That question is surprisingly hard to answer off the top of your head. A
            bracket makes it easier by breaking the decision into a series of smaller, one-on-one
            comparisons. By the time you reach the final, you've genuinely considered every song
            rather than defaulting to the most obvious hit.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Music Brackets is built and maintained by the team behind{" "}
            <Box component="a" href="https://www.musicrandomizer.com" target="_blank" sx={{ color: "primary.main" }}>
              MusicRandomizer.com
            </Box>
            , a suite of free tools for music discovery and engagement. Our database covers thousands
            of artists across every major genre, and we add new music regularly.
          </Typography>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Typography variant="h5" textAlign="center" gutterBottom>
        How It Works
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        <Card sx={{ backgroundColor: "rgba(52,152,219,0.04)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <QueueMusicIcon sx={{ fontSize: 44, color: "#3498db", mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              1. Choose Your Songs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick a game mode and select your songs. Search for a specific artist, let us build a
              random bracket, or choose two artists for a versus matchup. Then pick your bracket size
              — 8, 16, 32, or 64 songs — and whether you want single or double elimination.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ backgroundColor: "rgba(231,76,60,0.04)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <HowToVoteIcon sx={{ fontSize: 44, color: "#e74c3c", mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              2. Vote on Matchups
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Songs are seeded and paired up. For each matchup you can listen to 30-second previews
              of both songs, then pick your favorite. The winner advances to the next round. In
              double elimination, losers drop to a second bracket and get another chance.
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ backgroundColor: "rgba(243,156,18,0.04)" }}>
          <CardContent sx={{ textAlign: "center" }}>
            <EmojiEventsIcon sx={{ fontSize: 44, color: "#f39c12", mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              3. See Your Results
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When the tournament is over, you'll see a full ranking of every song from first place
              to last. Save your results to your account, share your bracket publicly on the Explore
              page, and compare your rankings with the community.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Game Modes */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography variant="h5" textAlign="center" gutterBottom>
        Game Modes
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, maxWidth: 600, mx: "auto" }}>
        Music Brackets offers three distinct ways to play. Each mode is designed for a different kind
        of music fan experience.
      </Typography>

      <Card sx={{ mb: 2, backgroundColor: "rgba(231,76,60,0.05)", border: "1px solid rgba(231,76,60,0.15)" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <MusicNoteIcon sx={{ color: "#e74c3c" }} />
            <Typography variant="h6">Artist Brackets</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Search for any artist in our database and we'll build a bracket from their songs.
            This is the most popular mode — perfect for finally answering "What's my favorite song
            by this artist?" Whether it's a deep discography with dozens of albums or a newer
            artist with a handful of hits, the bracket adapts to the catalog. Songs are randomized
            each time, so replaying the same artist gives you a fresh experience.
          </Typography>
        </CardContent>
      </Card>
      <Card sx={{ mb: 2, backgroundColor: "rgba(52,152,219,0.05)", border: "1px solid rgba(52,152,219,0.15)" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ShuffleIcon sx={{ color: "#3498db" }} />
            <Typography variant="h6">Custom Games</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Custom games pull random songs from across our entire database, mixing genres, decades,
            and artists into one bracket. You might compare a classic rock anthem with a modern
            pop hit or a hip-hop banger with an indie ballad. It's unpredictable, fun, and a
            great way to discover music you wouldn't normally listen to.
          </Typography>
        </CardContent>
      </Card>
      <Card sx={{ mb: 4, backgroundColor: "rgba(108,70,117,0.05)", border: "1px solid rgba(108,70,117,0.15)" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PeopleIcon sx={{ color: "#6C4675" }} />
            <Typography variant="h6">Artist vs Artist</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Choose two artists and we'll combine their songs into a single bracket. Half the
            songs come from each artist, and the seeding is shuffled so you'll get matchups
            both within and across artists. This mode is built for settling debates — Beatles
            vs Stones, Drake vs Kendrick, Taylor Swift vs Beyonce. Pick your two favorites and
            let the songs decide.
          </Typography>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Typography variant="h5" textAlign="center" gutterBottom>
        Frequently Asked Questions
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
        Everything you need to know about playing Music Brackets.
      </Typography>

      <Box sx={{ mb: 4 }}>
        {faqs.map((faq) => (
          <Accordion
            key={faq.q}
            disableGutters
            sx={{
              backgroundColor: "rgba(255,255,255,0.02)",
              "&:before": { display: "none" },
              border: "1px solid rgba(255,255,255,0.06)",
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Contact */}
      <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: "auto" }}>
          Have a question, suggestion, or issue? We'd love to hear from you. Reach out at{" "}
          <Box component="a" href="mailto:support@musicrandomizer.com" sx={{ color: "primary.main" }}>
            support@musicrandomizer.com
          </Box>{" "}
          or follow us on{" "}
          <Box component="a" href="https://x.com/musicrandomized" target="_blank" sx={{ color: "primary.main" }}>
            X (Twitter)
          </Box>
          .
        </Typography>
      </Box>
    </Box>
  );
}
